<script>
import AddSLA from './AddSLA.vue';
import EditSLA from './EditSLA.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SLAEmptyState from './components/SLAEmptyState.vue';
import SLAHeader from './components/SLAHeader.vue';
import SLAListItem from './components/SLAListItem.vue';
import SLAListItemLoading from './components/SLAListItemLoading.vue';
import SLAPaywallEnterprise from './components/SLAPaywallEnterprise.vue';

import { mapGetters } from 'vuex';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    AddSLA,
    EditSLA,
    SettingsLayout,
    SLAEmptyState,
    SLAHeader,
    SLAListItem,
    SLAListItemLoading,
    SLAPaywallEnterprise,
  },
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      showHardDeleteConfirmationPopup: false,
      selectedResponse: {},
    };
  },
  computed: {
    ...mapGetters({
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      records: 'sla/getSLA',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      uiFlags: 'sla/getUIFlags',
    }),
    deleteConfirmText() {
      return this.$t('SLA.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('SLA.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}`;
    },
    isBehindAPaywall() {
      return !this.isFeatureEnabledonAccount(this.accountId, 'sla');
    },
    isSuperAdmin() {
      return this.currentUser.type === 'SuperAdmin';
    },
    activeSLAs() {
      return this.records.filter(sla => !sla.disabled);
    },
    deletedSLAs() {
      return this.records.filter(sla => sla.disabled);
    },
  },
  watch: {
    records: {
      handler(newRecords) {
        // Update selectedResponse if modal is open and we have a matching SLA
        if (this.showEditPopup && this.selectedResponse?.id) {
          const updatedSLA = newRecords.find(
            record => record.id === this.selectedResponse.id
          );
          if (updatedSLA) {
            this.selectedResponse = updatedSLA;
          }
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.$store.dispatch('sla/get');
  },
  methods: {
    openAddPopup() {
      if (this.isBehindAPaywall) {
        return;
      }
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(sla) {
      if (this.isBehindAPaywall) {
        return;
      }
      // Get fresh data from store to ensure we have the latest values
      const freshSLA = this.records.find(record => record.id === sla.id) || sla;
      this.selectedResponse = freshSLA;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
      this.selectedResponse = {};
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    openHardDeletePopup(response) {
      this.showHardDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeHardDeletePopup() {
      this.showHardDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteSla(this.selectedResponse.id);
    },
    confirmHardDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeHardDeletePopup();
      this.hardDeleteSla(this.selectedResponse.id);
    },
    deleteSla(id) {
      this.$store
        .dispatch('sla/softDelete', id)
        .then(() => {
          useAlert(this.$t('SLA.DEACTIVATE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          useAlert(this.$t('SLA.DEACTIVATE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[id] = false;
        });
    },
    reactivateSla(id) {
      this.loading[id] = true;
      this.$store
        .dispatch('sla/reactivate', id)
        .then(() => {
          useAlert(this.$t('SLA.REACTIVATE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          useAlert(this.$t('SLA.REACTIVATE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[id] = false;
        });
    },
    hardDeleteSla(id) {
      this.$store
        .dispatch('sla/hardDelete', id)
        .then(() => {
          useAlert(this.$t('SLA.HARD_DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          useAlert(this.$t('SLA.HARD_DELETE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[id] = false;
        });
    },
    displayTime(threshold) {
      const { time, unit } = convertSecondsToTimeUnit(threshold, {
        minute: 'm',
        hour: 'h',
        day: 'd',
      });
      if (!time) return '-';
      return `${time}${unit}`;
    },
    onClickCTA() {
      this.$router.push({
        name: 'billing_settings_index',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SLA.LOADING')"
  >
    <template #header>
      <SLAHeader :show-actions="records.length > 0" @add="openAddPopup" />
    </template>
    <template #loading>
      <SLAListItemLoading v-for="ii in 2" :key="ii" class="mb-3" />
    </template>
    <template #body>
      <SLAPaywallEnterprise
        v-if="isBehindAPaywall"
        :is-super-admin="isSuperAdmin"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @upgrade="onClickCTA"
      />
      <SLAEmptyState
        v-else-if="!records.length"
        @primary-action="openAddPopup"
      />
      <div v-else class="flex flex-col w-full h-full gap-3">
        <!-- Active SLAs -->
        <SLAListItem
          v-for="sla in activeSLAs"
          :key="sla.id"
          :sla="sla"
          :sla-name="sla.name"
          :description="sla.description"
          :first-response="displayTime(sla.first_response_time_threshold)"
          :next-response="displayTime(sla.next_response_time_threshold)"
          :resolution-time="displayTime(sla.resolution_time_threshold)"
          :has-business-hours="sla.only_during_business_hours"
          :is-loading="loading[sla.id]"
          :is-deleted="false"
          @delete="openDeletePopup(sla)"
          @edit="openEditPopup(sla)"
        />
        <!-- Soft-deleted SLAs -->
        <div
          v-if="deletedSLAs.length > 0"
          class="flex flex-col w-full h-full gap-3 border-t border-n-weak pt-6"
        >
          <h3 class="text-sm font-semibold text-n-slate-11 mb-3">
            {{ $t('SLA.DEACTIVATED_SECTION.TITLE') }}
          </h3>
          <SLAListItem
            v-for="sla in deletedSLAs"
            :key="sla.id"
            :sla="sla"
            :sla-name="sla.name"
            :description="sla.description"
            :first-response="displayTime(sla.first_response_time_threshold)"
            :next-response="displayTime(sla.next_response_time_threshold)"
            :resolution-time="displayTime(sla.resolution_time_threshold)"
            :has-business-hours="sla.only_during_business_hours"
            :is-loading="loading[sla.id]"
            is-deleted
            @reactivate="reactivateSla(sla.id)"
            @hard-delete="openHardDeletePopup(sla)"
          />
        </div>
      </div>

      <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
        <AddSLA @close="hideAddPopup" />
      </woot-modal>

      <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
        <EditSLA :selected-response="selectedResponse" @close="hideEditPopup" />
      </woot-modal>

      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('SLA.DEACTIVATE.CONFIRM.TITLE')"
        :message="$t('SLA.DEACTIVATE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('SLA.DEACTIVATE.CONFIRM.YES')"
        :reject-text="$t('SLA.DEACTIVATE.CONFIRM.NO')"
      />

      <woot-delete-modal
        v-model:show="showHardDeleteConfirmationPopup"
        :on-close="closeHardDeletePopup"
        :on-confirm="confirmHardDeletion"
        :title="$t('SLA.HARD_DELETE.CONFIRM.TITLE')"
        :message="$t('SLA.HARD_DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('SLA.HARD_DELETE.CONFIRM.YES')"
        :reject-text="$t('SLA.HARD_DELETE.CONFIRM.NO')"
      />
    </template>
  </SettingsLayout>
</template>
